import * as XLSX from "xlsx";

export function asText(value) {
  return value == null ? "" : String(value).trim();
}

export function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value.toDate === "function") {
    const converted = value.toDate();
    return converted instanceof Date && !Number.isNaN(converted.getTime())
      ? converted
      : null;
  }
  if (Number.isFinite(value.seconds)) {
    const converted = new Date(value.seconds * 1000);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }
  const converted = new Date(value);
  return Number.isNaN(converted.getTime()) ? null : converted;
}

export function localDateInputValue(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function dateRangeFromInputs(dateFrom, dateTo) {
  const start = new Date(`${dateFrom}T00:00:00.000`);
  const end = new Date(`${dateTo}T23:59:59.999`);
  if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime()) ||
    start > end
  ) {
    return null;
  }
  return { start, end };
}

export function isWithinDateRange(value, start, end) {
  const date = toDate(value);
  return Boolean(date && date >= start && date <= end);
}

export function formatDateTime(value) {
  const date = toDate(value);
  return date ? date.toLocaleString("en-GB") : "";
}

function formatLongDate(dateInput) {
  const date = new Date(`${dateInput}T12:00:00`);
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function formatPeriodLabel(dateFrom, dateTo) {
  if (dateFrom === dateTo) return `Report for ${formatLongDate(dateFrom)}`;
  return `Report for ${formatLongDate(dateFrom)} to ${formatLongDate(dateTo)}`;
}

export function reconstructBarcode(serialNumber, special, productId) {
  const serial = asText(serialNumber);
  const type = asText(special);
  const product = asText(productId);
  if (
    !/^\d{6}$/.test(serial) ||
    !/^\d{2}$/.test(type) ||
    !/^\d{5}$/.test(product)
  ) {
    return "";
  }
  return `${serial}${type}${product}`;
}

export function buildProductMap(productDocuments) {
  return new Map(
    (productDocuments || [])
      .map((product) => [
        asText(product.id),
        {
          productCode: asText(product.productCode),
          productDescription: asText(product.productDescription),
        },
      ])
      .filter(([id]) => id)
  );
}

export function buildLocationMap(locationDocuments) {
  const locations = new Map();
  for (const location of locationDocuments || []) {
    const barcode = asText(location.barcode);
    if (barcode) {
      locations.set(barcode.slice(-5), asText(location.id) || barcode);
    }
  }
  return locations;
}

function resolveLocation(locationId, locationMap) {
  const raw = asText(locationId);
  if (!raw) return "";
  return locationMap?.get(raw.slice(-5)) || raw;
}

export function isClearlyWarehouseBackup(record) {
  if (
    record?.Backup === true ||
    record?.IsBackup === true ||
    record?.SystemGeneratedBackup === true ||
    record?.WarehouseBackup === true
  ) {
    return true;
  }

  const reasonAndMethod = [
    record?.Reason,
    record?.reason,
    record?.BookedOutMethod,
    record?.bookedOutMethod,
    record?.ArchiveType,
    record?.archiveType,
  ]
    .map(asText)
    .join(" ")
    .toLowerCase();

  if (
    reasonAndMethod.includes("backup") ||
    reasonAndMethod.includes("pre-sync") ||
    reasonAndMethod.includes("pre sync") ||
    reasonAndMethod.includes("pre-allocated") ||
    reasonAndMethod.includes("pre allocated") ||
    reasonAndMethod.includes("pre-allocation") ||
    reasonAndMethod.includes("pre allocation")
  ) {
    return true;
  }

  const source = asText(record?.source || record?.Source).toLowerCase();
  if (
    source === "stocktake" ||
    source === "cylinders_warehouse_duplicate" ||
    (source === "warehouse" && asText(record?.id))
  ) {
    return true;
  }

  return false;
}

export function buildDespatchedItems({
  reports,
  details,
  productMap,
  start,
  end,
}) {
  const completedReports = new Map(
    (reports || [])
      .filter((report) => report.completed === true)
      .map((report) => [asText(report.id), report])
  );
  const items = [];

  for (const detail of details || []) {
    const report = completedReports.get(asText(detail.id));
    if (!report) continue;

    const reportItems = Array.isArray(detail.items) ? detail.items : [];
    reportItems.forEach((item, index) => {
      if (!isWithinDateRange(item.scannedAt, start, end)) return;

      const productId = asText(item.productId);
      const product = productMap?.get(productId);
      const embeddedProductCode = asText(item.productCode);
      const productCode =
        embeddedProductCode && !/^Unknown/i.test(embeddedProductCode)
          ? embeddedProductCode
          : asText(product?.productCode) || embeddedProductCode;

      items.push({
        id: `${asText(report.id)}:${asText(item.id) || index}`,
        scannedAt: toDate(item.scannedAt),
        reportNumber: asText(report.reportNumber),
        barcode: asText(item.barcode),
        serialNumber: asText(item.serialNumber),
        productCode,
        productId,
        special: asText(item.special),
      });
    });
  }

  return items.sort(
    (left, right) => right.scannedAt.getTime() - left.scannedAt.getTime()
  );
}

export function buildWarehouseBookedOutItems({
  archiveRecords,
  productMap,
  locationMap,
  start,
  end,
}) {
  const items = [];
  let excludedBackupCount = 0;

  for (const record of archiveRecords || []) {
    if (!isWithinDateRange(record.BookedOutTime, start, end)) continue;
    if (isClearlyWarehouseBackup(record)) {
      excludedBackupCount += 1;
      continue;
    }

    const productId = asText(record.ProductId);
    const special = asText(record.Special);
    const product = productMap?.get(productId);
    items.push({
      id: asText(record.id),
      bookedOutAt: toDate(record.BookedOutTime),
      barcode: reconstructBarcode(
        record.SerialNumber,
        special,
        productId
      ),
      serialNumber: asText(record.SerialNumber),
      productCode: asText(product?.productCode),
      productId,
      special,
      location: resolveLocation(record.LocationId, locationMap),
      reason: asText(record.ManualBookOutReason || record.Reason),
      notes: asText(record.ManualBookOutNotes),
    });
  }

  items.sort(
    (left, right) =>
      right.bookedOutAt.getTime() - left.bookedOutAt.getTime()
  );
  return { items, excludedBackupCount };
}

function markTextColumns(worksheet, textColumnIndexes, rowCount) {
  for (const columnIndex of textColumnIndexes) {
    for (let rowIndex = 1; rowIndex <= rowCount; rowIndex += 1) {
      const address = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
      const cell = worksheet[address];
      if (!cell) continue;
      cell.t = "s";
      cell.v = asText(cell.v);
      cell.z = "@";
    }
  }
}

export function buildDespatchWarehouseWorkbook({
  dateFrom,
  dateTo,
  despatchedItems,
  warehouseItems,
  generatedAt = new Date(),
}) {
  const summarySheet = XLSX.utils.aoa_to_sheet([
    ["Date from", dateFrom],
    ["Date to", dateTo],
    ["Despatched count", despatchedItems.length],
    ["Warehouse booked-out count", warehouseItems.length],
    ["Generated at", generatedAt.toISOString()],
  ]);

  const despatchRows = [
    [
      "Date and time",
      "Despatch report number",
      "Barcode",
      "Serial number",
      "Product code",
      "Product ID",
      "Type / special",
    ],
    ...despatchedItems.map((item) => [
      formatDateTime(item.scannedAt),
      item.reportNumber,
      item.barcode,
      item.serialNumber,
      item.productCode,
      item.productId,
      item.special,
    ]),
  ];
  const despatchSheet = XLSX.utils.aoa_to_sheet(despatchRows);
  markTextColumns(
    despatchSheet,
    [1, 2, 3, 4, 5, 6],
    despatchedItems.length
  );

  const warehouseRows = [
    [
      "Booked-out date and time",
      "Barcode",
      "Serial number",
      "Product code",
      "Product ID",
      "Type / special",
      "Last warehouse location",
      "Removal reason",
      "Notes",
    ],
    ...warehouseItems.map((item) => [
      formatDateTime(item.bookedOutAt),
      item.barcode,
      item.serialNumber,
      item.productCode,
      item.productId,
      item.special,
      item.location,
      item.reason,
      item.notes,
    ]),
  ];
  const warehouseSheet = XLSX.utils.aoa_to_sheet(warehouseRows);
  markTextColumns(
    warehouseSheet,
    [1, 2, 3, 4, 5, 6],
    warehouseItems.length
  );

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
  XLSX.utils.book_append_sheet(
    workbook,
    despatchSheet,
    "Despatched items"
  );
  XLSX.utils.book_append_sheet(
    workbook,
    warehouseSheet,
    "Warehouse booked out"
  );

  const filename =
    dateFrom === dateTo
      ? `despatch-warehouse-report-${dateFrom}.xlsx`
      : `despatch-warehouse-report-${dateFrom}-to-${dateTo}.xlsx`;

  return { workbook, filename };
}

export function exportDespatchWarehouseWorkbook(options) {
  const { workbook, filename } = buildDespatchWarehouseWorkbook(options);
  XLSX.writeFile(workbook, filename);
}
